const DISPLAY_DATE_REGEX = /^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2})(?:\s*([AP]M))?$/i;
const ISO_DATE_WITHOUT_TIMEZONE_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,7})?)?$/;
const SQL_NAIVE_DATETIME_REGEX =
  /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,7})?)?$/;

function parseWallClockDateParts(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number
): Date | null {
  if (
    Number.isNaN(year) ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    Number.isNaN(hour) ||
    Number.isNaN(minute) ||
    Number.isNaN(second)
  ) {
    return null;
  }

  const parsed = new Date(year, month - 1, day, hour, minute, second, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function parseNaiveApiDateTime(value: string): Date | null {
  const match = value.trim().match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/
  );
  if (!match) {
    return null;
  }

  return parseWallClockDateParts(
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
    Number(match[6] || 0)
  );
}

function hasExplicitTimezone(value: string): boolean {
  return /(?:Z|[+-]\d{2}:\d{2})$/i.test(value.trim());
}

function parseAmmDisplayDate(value: string): Date | null {
  const match = value.trim().match(DISPLAY_DATE_REGEX);
  if (!match) {
    return null;
  }

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  let hour = Number(match[4]);
  const minute = Number(match[5]);
  const ampm = (match[6] || '').toUpperCase();

  if (
    Number.isNaN(day) ||
    Number.isNaN(month) ||
    Number.isNaN(year) ||
    Number.isNaN(hour) ||
    Number.isNaN(minute)
  ) {
    return null;
  }

  if (ampm === 'PM' && hour < 12) {
    hour += 12;
  } else if (ampm === 'AM' && hour === 12) {
    hour = 0;
  }

  const parsed = new Date(year, month - 1, day, hour, minute, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function parseInputDate(value: string | Date): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }

  const raw = String(value || '').trim();
  if (!raw) {
    return null;
  }

  const ammDisplayDate = parseAmmDisplayDate(raw);
  if (ammDisplayDate) {
    return ammDisplayDate;
  }

  // The API stores Qatar wall-clock values in timestamp columns without timezone.
  // Treat naive ISO/SQL values as wall-clock; do not append "Z" (that adds +3h in Qatar).
  if (
    !hasExplicitTimezone(raw) &&
    (ISO_DATE_WITHOUT_TIMEZONE_REGEX.test(raw) || SQL_NAIVE_DATETIME_REGEX.test(raw))
  ) {
    return parseNaiveApiDateTime(raw);
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeForViewLog(value: string | Date, entityType: 'file' | 'folder'): Date | null {
  const parsed = parseInputDate(value);
  if (!parsed) {
    return null;
  }
  return parsed;
}

function twoDigits(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

function to12Hour(hours24: number): { hour: string; period: 'AM' | 'PM' } {
  const period: 'AM' | 'PM' = hours24 >= 12 ? 'PM' : 'AM';
  const normalized = hours24 % 12 || 12;
  return { hour: twoDigits(normalized), period };
}

export function formatAmmViewLogDateTime(
  value: string | Date | null | undefined,
  entityType: 'file' | 'folder' = 'file'
): string {
  if (value === undefined || value === null || String(value).trim() === '') {
    return '-';
  }

  const date = normalizeForViewLog(value, entityType);
  if (!date) {
    return '-';
  }

  const day = twoDigits(date.getDate());
  const month = twoDigits(date.getMonth() + 1);
  const year = date.getFullYear();
  const { hour, period } = to12Hour(date.getHours());
  const minutes = twoDigits(date.getMinutes());

  return `${day}/${month}/${year}, ${hour}:${minutes} ${period}`;
}

export function toAmmViewLogTimestamp(
  value: string | Date | null | undefined,
  entityType: 'file' | 'folder' = 'file'
): number {
  if (value === undefined || value === null || String(value).trim() === '') {
    return 0;
  }

  const date = normalizeForViewLog(value, entityType);
  return date ? date.getTime() : 0;
}
